import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  Chip,
  Switch,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  People as FamilyIcon,
  Assignment as AssignmentIcon,
  School as SchoolIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

import Swal from 'sweetalert2';
import { SwalConfig } from '../../utils/swalConfig';

// Project import
import { candidatesData } from '../../Data/Kontak/Candidate';

const EditCandidate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(null);

  const steps = [
    'Data Pribadi',
    'Riwayat', 
    'Riwayat Pekerjaan',
    'Keluarga',
    'Lamaran & Kemampuan',
    'Pendidikan & Umum',
    'Lain-lain'
  ];

  useEffect(() => {
    const fetchCandidate = async () => {
      try {
        const response = await fetch(`https://apidashboard.eurekagroup.id/candidates/${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        const candidateData = result.data || result;
        // Map _id to id for frontend compatibility
        const mappedCandidate = {
          ...candidateData,
          id: candidateData._id || candidateData.id
        };
        setFormData(mappedCandidate);
        console.log('Candidate loaded:', result);
      } catch (error) {
        console.error('Error fetching candidate:', error);
        // Fallback to static data
        const candidate = candidatesData.find(c => c.id === parseInt(id));
        if (candidate) {
          setFormData({ ...candidate });
        }
        Swal.fire({
          ...SwalConfig.error,
          title: 'Warning!',
          text: 'Gagal memuat data dari server, menggunakan data lokal'
        });
      }
    };

    fetchCandidate();
  }, [id]);

  if (!formData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography variant="h6">Kandidat tidak ditemukan</Typography>
      </Box>
    );
  }

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(`https://apidashboard.eurekagroup.id/candidates/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Candidate updated:', result);
      
      SwalConfig.success(
        'Berhasil Diperbarui!',
        `Data kandidat ${formData.personalData.full_name} berhasil diperbarui.`
      ).then(() => {
        navigate('/kontak/candidate');
      });
    } catch (error) {
      console.error('Error updating candidate:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal memperbarui kandidat: ${error.message}`
      });
    }
  };

  const renderPersonalDataForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }} className="animate-slide-up">
      <CardHeader
        avatar={<Avatar sx={{ bgcolor: 'primary.main' }}><PersonIcon /></Avatar>}
        title="Data Pribadi"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nama Lengkap"
              value={formData.personalData.full_name}
              onChange={(e) => handleInputChange('personalData', 'full_name', e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Jenis Kelamin</InputLabel>
              <Select
                value={formData.personalData.gender}
                onChange={(e) => handleInputChange('personalData', 'gender', e.target.value)}
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Tempat Lahir"
              value={formData.personalData.birth_place}
              onChange={(e) => handleInputChange('personalData', 'birth_place', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Tanggal Lahir"
              type="date"
              value={formData.personalData.birth_date}
              onChange={(e) => handleInputChange('personalData', 'birth_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Agama"
              value={formData.personalData.religion}
              onChange={(e) => handleInputChange('personalData', 'religion', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Suku"
              value={formData.personalData.ethnicity}
              onChange={(e) => handleInputChange('personalData', 'ethnicity', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Usia"
              type="number"
              value={formData.personalData.age}
              onChange={(e) => handleInputChange('personalData', 'age', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nomor Telepon"
              value={formData.personalData.phone}
              onChange={(e) => handleInputChange('personalData', 'phone', e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Alamat"
              multiline
              rows={2}
              value={formData.personalData.address}
              onChange={(e) => handleInputChange('personalData', 'address', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Prestasi Terbaik"
              value={formData.personalData.best_achievement}
              onChange={(e) => handleInputChange('personalData', 'best_achievement', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Deskripsi Prestasi Terbaik"
              value={formData.personalData.best_achievement_desc}
              onChange={(e) => handleInputChange('personalData', 'best_achievement_desc', e.target.value)}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderHistoryForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }} className="animate-slide-up">
      <CardHeader
        avatar={<Avatar sx={{ bgcolor: 'info.main' }}><InfoIcon /></Avatar>}
        title="Riwayat"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Alasan Keluar Kerja Terakhir"
              value={formData.history.last_job_reason_leave}
              onChange={(e) => handleInputChange('history', 'last_job_reason_leave', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Gaji Terakhir"
              value={formData.history.last_job_salary}
              onChange={(e) => handleInputChange('history', 'last_job_salary', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Pekerjaan yang Disukai"
              value={formData.history.job_most_liked}
              onChange={(e) => handleInputChange('history', 'job_most_liked', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Pekerjaan yang Tidak Disukai"
              value={formData.history.job_least_liked}
              onChange={(e) => handleInputChange('history', 'job_least_liked', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Hobi"
              value={formData.history.hobby}
              onChange={(e) => handleInputChange('history', 'hobby', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Waktu Luang"
              value={formData.history.free_time}
              onChange={(e) => handleInputChange('history', 'free_time', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Topik Bacaan"
              value={formData.history.reading_topics}
              onChange={(e) => handleInputChange('history', 'reading_topics', e.target.value)}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const addWorkHistory = () => {
    const newWork = {
      masuk: '',
      keluar: '',
      jabatan: '',
      perusahaan: '',
      alamat_telp: '',
      keterangan: ''
    };
    setFormData(prev => ({
      ...prev,
      workHistory: [...prev.workHistory, newWork]
    }));
  };

  const updateWorkHistory = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      workHistory: prev.workHistory.map((work, i) => 
        i === index ? { ...work, [field]: value } : work
      )
    }));
  };

  const removeWorkHistory = (index) => {
    setFormData(prev => ({
      ...prev,
      workHistory: prev.workHistory.filter((_, i) => i !== index)
    }));
  };

  const renderWorkHistoryForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }} className="animate-slide-up">
      <CardHeader
        avatar={<Avatar sx={{ bgcolor: 'warning.main' }}><WorkIcon /></Avatar>}
        title="Riwayat Pekerjaan"
        titleTypographyProps={{ fontWeight: 600 }}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addWorkHistory}
            size="small"
          >
            Tambah Pekerjaan
          </Button>
        }
      />
      <CardContent>
        {formData.workHistory.map((work, index) => (
          <Card key={index} sx={{ mb: 2, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Pekerjaan {index + 1}
              <IconButton 
                onClick={() => removeWorkHistory(index)} 
                sx={{ float: 'right', color: 'error.main' }}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tanggal Masuk"
                  type="date"
                  value={work.masuk}
                  onChange={(e) => updateWorkHistory(index, 'masuk', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tanggal Keluar"
                  type="date"
                  value={work.keluar}
                  onChange={(e) => updateWorkHistory(index, 'keluar', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Jabatan"
                  value={work.jabatan}
                  onChange={(e) => updateWorkHistory(index, 'jabatan', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Perusahaan/Instansi"
                  value={work.perusahaan}
                  onChange={(e) => updateWorkHistory(index, 'perusahaan', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Alamat & Telepon"
                  value={work.alamat_telp}
                  onChange={(e) => updateWorkHistory(index, 'alamat_telp', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Keterangan"
                  multiline
                  rows={2}
                  value={work.keterangan}
                  onChange={(e) => updateWorkHistory(index, 'keterangan', e.target.value)}
                  size="small"
                />
              </Grid>
            </Grid>
          </Card>
        ))}
        {formData.workHistory.length === 0 && (
          <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
            Belum ada riwayat pekerjaan. Klik "Tambah Pekerjaan" untuk menambahkan.
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const addFamilyMember = () => {
    const newMember = {
      jenis: '',
      nama: '',
      gender: '',
      usia: '',
      pendidikan_terakhir: '',
      jabatan: '',
      pekerjaan: '',
      perusahaan: '',
      keterangan: ''
    };
    setFormData(prev => ({
      ...prev,
      family: {
        ...prev.family,
        members: [...prev.family.members, newMember]
      }
    }));
  };

  const updateFamilyMember = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      family: {
        ...prev.family,
        members: prev.family.members.map((member, i) => 
          i === index ? { ...member, [field]: value } : member
        )
      }
    }));
  };

  const removeFamilyMember = (index) => {
    setFormData(prev => ({
      ...prev,
      family: {
        ...prev.family,
        members: prev.family.members.filter((_, i) => i !== index)
      }
    }));
  };

  const renderFamilyForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }} className="animate-slide-up">
      <CardHeader
        avatar={<Avatar sx={{ bgcolor: 'secondary.main' }}><FamilyIcon /></Avatar>}
        title="Keluarga"
        titleTypographyProps={{ fontWeight: 600 }}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addFamilyMember}
            size="small"
          >
            Tambah Anggota Keluarga
          </Button>
        }
      />
      <CardContent>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Status Pernikahan</InputLabel>
              <Select
                value={formData.family.marital_status}
                onChange={(e) => handleInputChange('family', 'marital_status', e.target.value)}
              >
                <MenuItem value="Single">Single</MenuItem>
                <MenuItem value="Married">Married</MenuItem>
                <MenuItem value="Divorced">Divorced</MenuItem>
                <MenuItem value="Widowed">Widowed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Tanggal Menikah"
              type="date"
              value={formData.family.marriage_date}
              onChange={(e) => handleInputChange('family', 'marriage_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Jumlah Pernikahan"
              type="number"
              value={formData.family.marriage_count}
              onChange={(e) => handleInputChange('family', 'marriage_count', e.target.value)}
            />
          </Grid>
        </Grid>

        {formData.family.members.map((member, index) => (
          <Card key={index} sx={{ mb: 2, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Anggota Keluarga {index + 1}
              <IconButton 
                onClick={() => removeFamilyMember(index)} 
                sx={{ float: 'right', color: 'error.main' }}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Jenis"
                  value={member.jenis}
                  onChange={(e) => updateFamilyMember(index, 'jenis', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Nama"
                  value={member.nama}
                  onChange={(e) => updateFamilyMember(index, 'nama', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>L/P</InputLabel>
                  <Select
                    value={member.gender}
                    onChange={(e) => updateFamilyMember(index, 'gender', e.target.value)}
                  >
                    <MenuItem value="L">L</MenuItem>
                    <MenuItem value="P">P</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Usia"
                  type="number"
                  value={member.usia}
                  onChange={(e) => updateFamilyMember(index, 'usia', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Pendidikan Terakhir"
                  value={member.pendidikan_terakhir}
                  onChange={(e) => updateFamilyMember(index, 'pendidikan_terakhir', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Jabatan"
                  value={member.jabatan}
                  onChange={(e) => updateFamilyMember(index, 'jabatan', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Pekerjaan"
                  value={member.pekerjaan}
                  onChange={(e) => updateFamilyMember(index, 'pekerjaan', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Perusahaan/Inst."
                  value={member.perusahaan}
                  onChange={(e) => updateFamilyMember(index, 'perusahaan', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Keterangan"
                  value={member.keterangan}
                  onChange={(e) => updateFamilyMember(index, 'keterangan', e.target.value)}
                  size="small"
                />
              </Grid>
            </Grid>
          </Card>
        ))}
        {formData.family.members.length === 0 && (
          <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
            Belum ada anggota keluarga. Klik "Tambah Anggota Keluarga" untuk menambahkan.
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const addEducation = () => {
    const newEducation = {
      jenjang: '',
      nama_sekolah: '',
      tempat: '',
      tahun_lulus: '',
      rata_rata_nilai: '',
      jurusan: ''
    };
    setFormData(prev => ({
      ...prev,
      education: [...prev.education, newEducation]
    }));
  };

  const updateEducation = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      education: prev.education.map((edu, i) => 
        i === index ? { ...edu, [field]: value } : edu
      )
    }));
  };

  const removeEducation = (index) => {
    setFormData(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index)
    }));
  };

  const addCourse = () => {
    const newCourse = {
      instruktur_lembaga: '',
      tempat: '',
      lama_pendidikan: '',
      yang_membiayai: '',
      keterangan: ''
    };
    setFormData(prev => ({
      ...prev,
      courses: [...prev.courses, newCourse]
    }));
  };

  const updateCourse = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      courses: prev.courses.map((course, i) => 
        i === index ? { ...course, [field]: value } : course
      )
    }));
  };

  const removeCourse = (index) => {
    setFormData(prev => ({
      ...prev,
      courses: prev.courses.filter((_, i) => i !== index)
    }));
  };

  const renderEducationForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }} className="animate-slide-up">
      <CardHeader
        avatar={<Avatar sx={{ bgcolor: 'info.main' }}><SchoolIcon /></Avatar>}
        title="Pendidikan & Umum"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        {/* Pendidikan Formal */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Pendidikan Formal
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addEducation}
              size="small"
            >
              Tambah Pendidikan
            </Button>
          </Box>
          
          {formData.education.map((edu, index) => (
            <Card key={index} sx={{ mb: 2, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Pendidikan {index + 1}
                <IconButton 
                  onClick={() => removeEducation(index)} 
                  sx={{ float: 'right', color: 'error.main' }}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Jenjang</InputLabel>
                    <Select
                      value={edu.jenjang}
                      onChange={(e) => updateEducation(index, 'jenjang', e.target.value)}
                    >
                      <MenuItem value="SD">SD</MenuItem>
                      <MenuItem value="SMP">SMP</MenuItem>
                      <MenuItem value="SMA">SMA</MenuItem>
                      <MenuItem value="D3">D3</MenuItem>
                      <MenuItem value="S1">S1</MenuItem>
                      <MenuItem value="S2">S2</MenuItem>
                      <MenuItem value="S3">S3</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Nama Sekolah"
                    value={edu.nama_sekolah}
                    onChange={(e) => updateEducation(index, 'nama_sekolah', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Tempat"
                    value={edu.tempat}
                    onChange={(e) => updateEducation(index, 'tempat', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Tahun Lulus"
                    value={edu.tahun_lulus}
                    onChange={(e) => updateEducation(index, 'tahun_lulus', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Rata-rata Nilai/IPK"
                    value={edu.rata_rata_nilai}
                    onChange={(e) => updateEducation(index, 'rata_rata_nilai', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={1}>
                  <TextField
                    fullWidth
                    label="Jurusan"
                    value={edu.jurusan}
                    onChange={(e) => updateEducation(index, 'jurusan', e.target.value)}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Card>
          ))}
        </Box>

        {/* Kursus */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Kursus
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addCourse}
              size="small"
            >
              Tambah Kursus
            </Button>
          </Box>
          
          {formData.courses.map((course, index) => (
            <Card key={index} sx={{ mb: 2, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Kursus {index + 1}
                <IconButton 
                  onClick={() => removeCourse(index)} 
                  sx={{ float: 'right', color: 'error.main' }}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Instruktur/Lembaga Pelaksana"
                    value={course.instruktur_lembaga}
                    onChange={(e) => updateCourse(index, 'instruktur_lembaga', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Tempat"
                    value={course.tempat}
                    onChange={(e) => updateCourse(index, 'tempat', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Lama Pendidikan"
                    value={course.lama_pendidikan}
                    onChange={(e) => updateCourse(index, 'lama_pendidikan', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Yang Membiayai"
                    value={course.yang_membiayai}
                    onChange={(e) => updateCourse(index, 'yang_membiayai', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Keterangan"
                    value={course.keterangan}
                    onChange={(e) => updateCourse(index, 'keterangan', e.target.value)}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Card>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const addEmergencyRelation = () => {
    const newRelation = {
      nama: '',
      usia: '',
      alamat: '',
      no_telepon: '',
      hubungan: '',
      keterangan: ''
    };
    setFormData(prev => ({
      ...prev,
      others: {
        ...prev.others,
        emergency_relations: [...prev.others.emergency_relations, newRelation]
      }
    }));
  };

  const updateEmergencyRelation = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      others: {
        ...prev.others,
        emergency_relations: prev.others.emergency_relations.map((rel, i) => 
          i === index ? { ...rel, [field]: value } : rel
        )
      }
    }));
  };

  const removeEmergencyRelation = (index) => {
    setFormData(prev => ({
      ...prev,
      others: {
        ...prev.others,
        emergency_relations: prev.others.emergency_relations.filter((_, i) => i !== index)
      }
    }));
  };

  const addFormerRelation = () => {
    const newRelation = {
      nama: '',
      usia: '',
      alamat: '',
      no_telepon: '',
      hubungan: '',
      keterangan: ''
    };
    setFormData(prev => ({
      ...prev,
      others: {
        ...prev.others,
        former_relations: [...prev.others.former_relations, newRelation]
      }
    }));
  };

  const updateFormerRelation = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      others: {
        ...prev.others,
        former_relations: prev.others.former_relations.map((rel, i) => 
          i === index ? { ...rel, [field]: value } : rel
        )
      }
    }));
  };

  const removeFormerRelation = (index) => {
    setFormData(prev => ({
      ...prev,
      others: {
        ...prev.others,
        former_relations: prev.others.former_relations.filter((_, i) => i !== index)
      }
    }));
  };

  const addGuarantor = () => {
    const newGuarantor = {
      nama: '',
      usia: '',
      alamat: '',
      no_telepon: '',
      hubungan: '',
      keterangan: ''
    };
    setFormData(prev => ({
      ...prev,
      others: {
        ...prev.others,
        guarantors: [...prev.others.guarantors, newGuarantor]
      }
    }));
  };

  const updateGuarantor = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      others: {
        ...prev.others,
        guarantors: prev.others.guarantors.map((guar, i) => 
          i === index ? { ...guar, [field]: value } : guar
        )
      }
    }));
  };

  const removeGuarantor = (index) => {
    setFormData(prev => ({
      ...prev,
      others: {
        ...prev.others,
        guarantors: prev.others.guarantors.filter((_, i) => i !== index)
      }
    }));
  };

  const renderOthersForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }} className="animate-slide-up">
      <CardHeader
        avatar={<Avatar sx={{ bgcolor: 'error.main' }}><InfoIcon /></Avatar>}
        title="Lain-lain"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        {/* Relasi saat berhalangan */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Relasi saat berhalangan
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addEmergencyRelation}
              size="small"
            >
              Tambah Relasi
            </Button>
          </Box>
          
          {formData.others.emergency_relations.map((relation, index) => (
            <Card key={index} sx={{ mb: 2, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Relasi {index + 1}
                <IconButton 
                  onClick={() => removeEmergencyRelation(index)} 
                  sx={{ float: 'right', color: 'error.main' }}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Nama"
                    value={relation.nama}
                    onChange={(e) => updateEmergencyRelation(index, 'nama', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Usia"
                    type="number"
                    value={relation.usia}
                    onChange={(e) => updateEmergencyRelation(index, 'usia', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Alamat Rumah/Kantor"
                    value={relation.alamat}
                    onChange={(e) => updateEmergencyRelation(index, 'alamat', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="No. Telepon"
                    value={relation.no_telepon}
                    onChange={(e) => updateEmergencyRelation(index, 'no_telepon', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Hubungan"
                    value={relation.hubungan}
                    onChange={(e) => updateEmergencyRelation(index, 'hubungan', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Keterangan"
                    value={relation.keterangan}
                    onChange={(e) => updateEmergencyRelation(index, 'keterangan', e.target.value)}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Card>
          ))}
        </Box>

        {/* Eks atasan/keluarga */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Eks atasan/keluarga
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addFormerRelation}
              size="small"
            >
              Tambah Eks Atasan
            </Button>
          </Box>
          
          {formData.others.former_relations.map((relation, index) => (
            <Card key={index} sx={{ mb: 2, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Eks Atasan {index + 1}
                <IconButton 
                  onClick={() => removeFormerRelation(index)} 
                  sx={{ float: 'right', color: 'error.main' }}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Nama"
                    value={relation.nama}
                    onChange={(e) => updateFormerRelation(index, 'nama', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Usia"
                    type="number"
                    value={relation.usia}
                    onChange={(e) => updateFormerRelation(index, 'usia', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Alamat Rumah/Kantor"
                    value={relation.alamat}
                    onChange={(e) => updateFormerRelation(index, 'alamat', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="No. Telepon"
                    value={relation.no_telepon}
                    onChange={(e) => updateFormerRelation(index, 'no_telepon', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Hubungan"
                    value={relation.hubungan}
                    onChange={(e) => updateFormerRelation(index, 'hubungan', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Keterangan"
                    value={relation.keterangan}
                    onChange={(e) => updateFormerRelation(index, 'keterangan', e.target.value)}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Card>
          ))}
        </Box>

        {/* Penjamin di PT Eureka */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Penjamin di PT Eureka
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addGuarantor}
              size="small"
            >
              Tambah Penjamin
            </Button>
          </Box>
          
          {formData.others.guarantors.map((guarantor, index) => (
            <Card key={index} sx={{ mb: 2, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Penjamin {index + 1}
                <IconButton 
                  onClick={() => removeGuarantor(index)} 
                  sx={{ float: 'right', color: 'error.main' }}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Nama"
                    value={guarantor.nama}
                    onChange={(e) => updateGuarantor(index, 'nama', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Usia"
                    type="number"
                    value={guarantor.usia}
                    onChange={(e) => updateGuarantor(index, 'usia', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Alamat Rumah/Kantor"
                    value={guarantor.alamat}
                    onChange={(e) => updateGuarantor(index, 'alamat', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="No. Telepon"
                    value={guarantor.no_telepon}
                    onChange={(e) => updateGuarantor(index, 'no_telepon', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Hubungan"
                    value={guarantor.hubungan}
                    onChange={(e) => updateGuarantor(index, 'hubungan', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Keterangan"
                    value={guarantor.keterangan}
                    onChange={(e) => updateGuarantor(index, 'keterangan', e.target.value)}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Card>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const renderApplicationSkillsForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }} className="animate-slide-up">
      <CardHeader
        avatar={<Avatar sx={{ bgcolor: 'success.main' }}><AssignmentIcon /></Avatar>}
        title="Lamaran & Keterampilan"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Posisi yang Dilamar"
              value={formData.applicationSkills.desired_position}
              onChange={(e) => handleInputChange('applicationSkills', 'desired_position', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Gaji yang Diharapkan"
              value={formData.applicationSkills.expected_salary}
              onChange={(e) => handleInputChange('applicationSkills', 'expected_salary', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Alasan Melamar"
              multiline
              rows={2}
              value={formData.applicationSkills.apply_reason}
              onChange={(e) => handleInputChange('applicationSkills', 'apply_reason', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Keahlian"
              value={formData.applicationSkills.skills}
              onChange={(e) => handleInputChange('applicationSkills', 'skills', e.target.value)}
              helperText="Pisahkan dengan tanda ;"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Program Komputer"
              value={formData.applicationSkills.computer_programs}
              onChange={(e) => handleInputChange('applicationSkills', 'computer_programs', e.target.value)}
              helperText="Pisahkan dengan tanda ;"
            />
          </Grid>
          
          {/* SIM Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }}>
              <Chip label="Surat Izin Mengemudi (SIM)" />
            </Divider>
          </Grid>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.personalData.has_sim || false}
                  onChange={(e) => handleInputChange('personalData', 'has_sim', e.target.checked)}
                />
              }
              label="Anda memiliki SIM?"
              sx={{ mb: 2 }}
            />
          </Grid>
          
          {formData.personalData.has_sim && (
            <Grid container spacing={2}>
              {/* SIM C */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, border: '1px solid', borderColor: 'grey.300' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                    SIM C
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formData.personalData.sim_c_number}
                            onChange={(e) => {
                              if (!e.target.checked) {
                                handleInputChange('personalData', 'sim_c_number', '');
                                handleInputChange('personalData', 'sim_c_valid_until', '');
                              }
                            }}
                          />
                        }
                        label="Valid"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="No. SIM C"
                        value={formData.personalData.sim_c_number || ''}
                        onChange={(e) => handleInputChange('personalData', 'sim_c_number', e.target.value)}
                        disabled={!formData.personalData.sim_c_number}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Masa Berlaku"
                        type="date"
                        value={formData.personalData.sim_c_valid_until || ''}
                        onChange={(e) => handleInputChange('personalData', 'sim_c_valid_until', e.target.value)}
                        disabled={!formData.personalData.sim_c_number}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              
              {/* SIM A */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, border: '1px solid', borderColor: 'grey.300' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                    SIM A
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formData.personalData.sim_a_number}
                            onChange={(e) => {
                              if (!e.target.checked) {
                                handleInputChange('personalData', 'sim_a_number', '');
                                handleInputChange('personalData', 'sim_a_valid_until', '');
                              }
                            }}
                          />
                        }
                        label="Valid"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="No. SIM A"
                        value={formData.personalData.sim_a_number || ''}
                        onChange={(e) => handleInputChange('personalData', 'sim_a_number', e.target.value)}
                        disabled={!formData.personalData.sim_a_number}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Masa Berlaku"
                        type="date"
                        value={formData.personalData.sim_a_valid_until || ''}
                        onChange={(e) => handleInputChange('personalData', 'sim_a_valid_until', e.target.value)}
                        disabled={!formData.personalData.sim_a_number}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              
              {/* SIM B */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, border: '1px solid', borderColor: 'grey.300' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                    SIM B
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formData.personalData.sim_b_number}
                            onChange={(e) => {
                              if (!e.target.checked) {
                                handleInputChange('personalData', 'sim_b_number', '');
                                handleInputChange('personalData', 'sim_b_valid_until', '');
                              }
                            }}
                          />
                        }
                        label="Valid"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="No. SIM B"
                        value={formData.personalData.sim_b_number || ''}
                        onChange={(e) => handleInputChange('personalData', 'sim_b_number', e.target.value)}
                        disabled={!formData.personalData.sim_b_number}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Masa Berlaku"
                        type="date"
                        value={formData.personalData.sim_b_valid_until || ''}
                        onChange={(e) => handleInputChange('personalData', 'sim_b_valid_until', e.target.value)}
                        disabled={!formData.personalData.sim_b_number}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderPersonalDataForm();
      case 1:
        return renderHistoryForm();
      case 2:
        return renderWorkHistoryForm();
      case 3:
        return renderFamilyForm();
      case 4:
        return renderApplicationSkillsForm();
      case 5:
        return renderEducationForm();
      case 6:
        return renderOthersForm();
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 2 }} className="animate-fade-in">
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton 
          onClick={() => navigate('/kontak/candidate')}
          sx={{ 
            bgcolor: 'grey.100',
            '&:hover': { bgcolor: 'grey.200' }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Edit Kandidat: {formData.personalData.full_name}
        </Typography>
      </Box>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }} className="animate-slide-up">
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Form Content */}
      <Box sx={{ mb: 3 }}>
        {renderStepContent(activeStep)}
      </Box>

      {/* Navigation Buttons */}
      <Paper sx={{ p: 3, borderRadius: 2 }} className="animate-slide-up">
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            variant="outlined"
            size="large"
          >
            Kembali
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                startIcon={<SaveIcon />}
                size="large"
                className="bg-gradient-to-r from-blue-500 to-green-600 hover:from-blue-600 hover:to-green-700"
                sx={{ 
                  px: 4,
                  py: 1.5,
                  fontWeight: 600
                }}
              >
                💾 Simpan Perubahan
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                size="large"
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                sx={{ 
                  px: 4,
                  py: 1.5,
                  fontWeight: 600
                }}
              >
                Selanjutnya ➡️
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default EditCandidate;